/**
 * NFC URL Parser Utility
 *
 * Parses ANYW3AR NFC URLs with the format:
 * https://app.anyw3ar.com/nfc/{productId}/{itemId}/{signature}
 *
 * Note: itemId is the sequential item number for each physical product
 *
 * @example
 * ```tsx
 * const parsed = nfcUrlParser.parse('https://app.anyw3ar.com/nfc/9320499085661/1/-p6aHyPwKraAH4tPE7RgkDCcyzUtb474r6DFQ38rB04pK2RVoVPHRf1RMCUoAJBjuGBxnWawdW7cNQBP4KmUAw');
 * // { productId: '9320499085661', variant: '1', signature: '-p6aHyP...', rawUrl: '...' }
 *
 * const displayId = parseNfcIdentifier('https://app.anyw3ar.com/nfc/9320499085661/42/abc...');
 * // Returns: "9320499085661/42"
 * ```
 */

export interface ParsedNfcUrl {
  /** Shopify product ID extracted from path */
  productId: string;
  /** Sequential item number for the product (previously called variant) */
  variant: string;
  /** ED25519 signature for NFC verification */
  signature: string;
  /** Original unparsed URL */
  rawUrl: string;
}

export class NfcUrlParserError extends Error {
  constructor(message: string, public code: 'INVALID_DOMAIN' | 'INVALID_PATH' | 'MISSING_SEGMENTS') {
    super(message);
    this.name = 'NfcUrlParserError';
  }
}

/**
 * NFC URL Parser
 *
 * Extracts product information from ANYW3AR NFC URLs using path segments.
 */
export const nfcUrlParser = {
  /**
   * Parse NFC URL into structured data
   *
   * @param url - Full NFC URL (https://app.anyw3ar.com/nfc/{productId}/{itemId}/{signature})
   * @returns Parsed NFC URL data
   * @throws {NfcUrlParserError} If URL format is invalid
   */
  parse(url: string): ParsedNfcUrl {
    try {
      const urlObj = new URL(url);

      // Get expected hostname from env var with fallback to production domain
      const nfcBaseUrl = import.meta.env.VITE_NFC_BASE_URL || 'https://app.anyw3ar.com';
      const expectedHostname = new URL(nfcBaseUrl).hostname;

      // Validate domain (exact match to avoid fake-domain attacks)
      if (urlObj.hostname !== expectedHostname) {
        throw new NfcUrlParserError(
          `Invalid NFC URL: must be ${expectedHostname} domain`,
          'INVALID_DOMAIN'
        );
      }

      // Parse path: /nfc/{productId}/{itemId}/{signature}
      // Split but keep empty strings to detect double slashes
      const rawPathParts = urlObj.pathname.split('/');
      // Remove leading empty string from initial '/'
      const pathParts = rawPathParts.slice(1);

      // Validate path structure
      if (pathParts[0] !== 'nfc') {
        throw new NfcUrlParserError(
          'Invalid NFC URL: path must start with /nfc/',
          'INVALID_PATH'
        );
      }

      // Filter out empty strings now for length check
      const nonEmptyParts = pathParts.filter(Boolean);

      if (nonEmptyParts.length !== 4) {
        throw new NfcUrlParserError(
          `Invalid NFC URL: expected /nfc/{productId}/{itemId}/{signature}, got ${nonEmptyParts.length - 1} segments`,
          'INVALID_PATH'
        );
      }

      const [, productId, variant, signature] = nonEmptyParts;

      // Validate segments
      if (!productId || !variant || !signature) {
        throw new NfcUrlParserError(
          'Invalid NFC URL: missing productId, itemId, or signature',
          'MISSING_SEGMENTS'
        );
      }

      // Decode URI components (handles %20 and other encoded chars)
      const decodedSignature = decodeURIComponent(signature);

      return {
        productId,
        variant,
        signature: decodedSignature,
        rawUrl: url,
      };
    } catch (error) {
      if (error instanceof NfcUrlParserError) {
        throw error;
      }

      // Handle URL constructor errors
      throw new NfcUrlParserError(
        `Failed to parse NFC URL: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'INVALID_PATH'
      );
    }
  },
};

/**
 * Parse NFC URL to extract productId and itemId for display
 *
 * Simple helper that returns a user-friendly display string in the format
 * "{productId}/{itemId}" without leading zeros or padding.
 *
 * @param nfcUrl - Full NFC URL from scan or API response
 * @returns Display string in format "{productId}/{itemId}" (e.g., "ABC123/42")
 *
 * @example
 * ```tsx
 * parseNfcIdentifier("https://app.anyw3ar.com/nfc/PROD-123/42/abc123...")
 * // Returns: "PROD-123/42"
 *
 * parseNfcIdentifier("https://app.anyw3ar.com/nfc/XYZ789/1/def456...")
 * // Returns: "XYZ789/1"
 *
 * parseNfcIdentifier("https://app.anyw3ar.com/nfc/ABC/1523/ghi789...")
 * // Returns: "ABC/1523"
 * ```
 */
export function parseNfcIdentifier(nfcUrl: string): string {
  try {
    const parsed = nfcUrlParser.parse(nfcUrl);
    return `${parsed.productId}/${parsed.variant}`;
  } catch (error) {
    // If parsing fails, return the full URL as fallback
    return nfcUrl;
  }
}
